<template>
  <div class="card border-top-start-radius-0 border-top-end-radius-0">
    <controle-pagamento-detalhes
      :studentIdProp="state.studentId"
      :studentNameProp="state.studentName"
      v-if="showForm"
      @fecharForm="formularioTermo(false)"
    />
    <div v-else class="card-body">
      <div class="row mb-3">
        <div class="col-12">
          <table class="table-bordered float-end text-sm">
            <tbody>
              <tr>
                <td class="text-center px-3">{{ $t('accumulatedAmountReceived') }}</td>
                <td class="text-center px-3">
                  {{ $filters.converteCentavosValor(state.totals.total) }}
                </td>
              </tr>
              <tr>
                <td class="text-center px-3">{{ $t('accumulatedReceivedMount') }}</td>
                <td class="text-center px-3">
                  {{ $filters.converteCentavosValor(state.totals.month) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="col-12 d-flex justify-content-center align-items-end">
          <div class="mx-2">
            <label class="form-label mt-2">{{ $t('referenceMount') }}</label>
            <select
              class="form-control form-control-sm custom-select"
              v-model="state.mesSelecionado"
            >
              <option
                v-for="(item, index) in state.months"
                :key="index"
                :value="item.numero"
                selected
              >
                {{ item.mes }}
              </option>
            </select>
          </div>
          <div class="mx-2">
            <label class="form-label mt-2">{{ $t('referenceYear') }}</label>
            <select
              class="form-control form-control-sm custom-select"
              v-model="state.anoSelecionado"
            >
              <option value="2023" selected>2023</option>
              <option value="2024">2024</option>
              <option value="2025">2025</option>
              <option value="2026">2026</option>
              <option value="2027">2027</option>
            </select>
          </div>
          <div class="mx-2">
            <button type="button" class="mb-0 btn bg-gradient-success btn-sm" @click="buscarDados">
              {{ $t('filter') }}
            </button>
          </div>
        </div>
      </div>

      <!-- =================== TABELA =================== -->
      <MyTable
        :searchable="false"
        :data="state.listaPagamento"
        :columns="colunas"
        :itemsPerPage="12"
      >
        <template v-slot:row="{ row, index }">
          <td class="text-sm" v-if="!hasSelectedStudent">
            {{ row.name }}
            <template v-if="row.price_per_class">
              <br />
              <span class="badge bg-info text-white" style="font-size: 0.7rem;">
                R$ {{ formatPrice(row.price_per_class) }}/aula
              </span>
            </template>
            <template v-if="row.consulting_price">
              <br />
              <span class="badge bg-success text-white" style="font-size: 0.7rem;">
                Consultoria: R$ {{ formatPrice(row.consulting_price) }}
              </span>
            </template>
          </td>
          <td class="text-sm text-center">
            {{ row.payment_day }}
          </td>
          <td class="text-sm text-center">
            <span v-if="row.status === $t('onTimePayment')">
              {{ $filters.datePtbr(row.payment_date) }}
            </span>
          </td>
          <td class="text-sm text-center">
            <template v-if="row.status === $t('onTimePayment')">
              {{ $filters.converteCentavosValor(row.payment_amount) }}
              <br />
              <span style="font-size: 0.8rem">{{ row.payment_form }}</span>
            </template>
          </td>
          <td
            class="text-sm text-center"
            :class="row.status === $t('latePayment') ? 'text-danger' : ''"
          >
            {{ row.status }}
            <template v-if="row.status === $t('latePayment') && row.payment_date !== null">
              <br />
              <span style="font-size: 0.75rem">
                {{ $t('lastPayment') }}: {{ $filters.datePtbr(row.payment_date) }}
              </span>
            </template>
          </td>

          <!-- ✅ Ações -->
          <td class="text-sm">
            <div class="float-end d-flex align-items-center gap-2">
              <!-- Botão visual de bloqueio manual -->
              <span class="pointer" :title="getBlockTooltip(row)" @click="toggleBlock(row, index)">
  <i
    class="far"
    :class="row.blockedIcon ? 'fa-check-circle text-primary' : 'fa-circle text-secondary'"
    style="font-size: 1.5rem"
  ></i>
</span>
              <span>
                <span
                  class="pointer"
                  :title="$t('details')"
                  @click="detalhes(row.id, row.name)"
                >
                  <i class="fas fa-bars text-secondary me-2"></i>
                  {{ $t('details') }}
                </span>
              </span>
            </div>
          </td>
        </template>
      </MyTable>
    </div>
  </div>
</template>

<script>
import MyTable from '@/components/MyTable.vue'
import { reactive, onMounted, ref, inject, computed } from 'vue'
import { useStore } from 'vuex'
import coachAdministrationRepository from '@/services/api/coachAdministrationRepository'
import alunoRepository from '@/services/api/alunoRepository' // 🔹 IMPORTAÇÃO
import TermoAdesaoForm from '@/views/pages/treinador/alunos/alunoTermoAdesaoForm.vue'
import ControlePagamentoDetalhes from '@/views/pages/treinador/administracao/controlePagamentoDetalhes.vue'
import moment from 'moment'
import { useI18n } from 'vue-i18n'

export default {
  components: {
    ControlePagamentoDetalhes,
    TermoAdesaoForm,
    MyTable,
  },
  setup() {
    const store = useStore()
    const swal = inject('$swal')
    const { t } = useI18n()

    const showForm = ref(false)

    // Verifica se há aluno selecionado na navbar
    const hasSelectedStudent = computed(() => {
      return !!store.getters.StudentManager
    })

    // Colunas dinâmicas - remove coluna do nome quando aluno está selecionado
    const colunas = computed(() => {
      const cols = []
      if (!hasSelectedStudent.value) {
        cols.push({ label: t('studentName'), field: 'name', sortable: 'name' })
      }
      cols.push(
        { label: t('paymentDay'), field: 'payment_day', sortable: 'payment_day' },
        { label: t('paymentDate'), field: 'payment_date', sortable: 'payment_date' },
        { label: t('paymentAmount'), field: 'payment_amount', sortable: 'payment_amount' },
        { label: t('status'), field: 'status', sortable: 'status' },
        { label: t('actions'), field: 'actions' }
      )
      return cols
    })

    const state = reactive({
      listaPagamento: [],
      mesSelecionado: moment().format('MM'),
      anoSelecionado: moment().format('YYYY'),
      studentId: 0,
      studentName: '',
      totals: { total: '', month: '' },
      months: [
        { mes: t('january'), numero: '01' },
        { mes: t('february'), numero: '02' },
        { mes: t('march'), numero: '03' },
        { mes: t('april'), numero: '04' },
        { mes: t('may'), numero: '05' },
        { mes: t('june'), numero: '06' },
        { mes: t('july'), numero: '07' },
        { mes: t('august'), numero: '08' },
        { mes: t('september'), numero: '09' },
        { mes: t('october'), numero: '10' },
        { mes: t('november'), numero: '11' },
        { mes: t('december'), numero: '12' },
      ],
    })

    function statusStudentPayment(payment_day, payment_reference) {
      if (parseInt(payment_day) < 10) payment_day = `0${payment_day}`
      const dataVencimento = `${state.anoSelecionado}-${state.mesSelecionado}-${payment_day}`
      const anoMes = `${state.anoSelecionado}-${state.mesSelecionado}`
      const today = moment().format('YYYY-MM-DD')
      let status = t('latePayment')
      if (dataVencimento > today || payment_reference === anoMes) {
        status = t('onTimePayment')
      }
      return status
    }

    function buscarDados() {
  store.commit('SET_SPLASH_SCREEN', true)
  const periodo = state.anoSelecionado + '-' + state.mesSelecionado

  coachAdministrationRepository
    .studentPaymentsGet(periodo)
    .then((response) => {
      store.commit('SET_SPLASH_SCREEN', false)
      if (response.data.success) {
        state.listaPagamento = response.data.data.map((obj) => {
          // Define status do pagamento
          obj.status = statusStudentPayment(obj.payment_day, obj.payment_reference)
          if (obj.payment_reference != null) {
            obj.payment_reference = obj.payment_reference.split('-').reverse().join('/')
          }

          // Mapeia o campo da API para o campo usado no template
          obj.bloqueado_manual = !!obj.manually_blocked

          // Calcula bloqueio financeiro
          const today = moment().format('YYYY-MM-DD')
          const dueDate = `${state.anoSelecionado}-${state.mesSelecionado}-${String(obj.payment_day).padStart(2,'0')}`
          const financialBlock = !obj.payment_date && moment(dueDate).isBefore(today, 'day')

          // ✅ ADICIONAR: Flag para identificar bloqueio financeiro
          obj.hasFinancialBlock = financialBlock

          // Define ícone de bloqueio (já está correto)
          obj.blockedIcon = obj.bloqueado_manual || financialBlock

          return obj
        })
      }
    })
    .catch((error) => {
      console.error(error)
      store.commit('SET_SPLASH_SCREEN', false)
    })

  coachAdministrationRepository
    .studentPaymentsTotalsGet(periodo)
    .then((response) => {
      if (response.data.success) {
        state.totals = response.data.data
      }
    })
    .catch((error) => console.error(error))
}


    function detalhes(id, name) {
      state.studentId = id
      state.studentName = name
      formularioTermo(true)
    }

    function formularioTermo(visible) {
      showForm.value = visible
      if (!showForm.value) {
        state.studentId = 0
        state.studentName = ''
        buscarDados()
      }
    }

 function toggleBlock(row, index) {
  const action = row.bloqueado_manual
    ? alunoRepository.manualUnblock
    : alunoRepository.manualBlock

  action(row.id)
    .then((res) => {
      if (res.data.success) {
        const itemIndex = state.listaPagamento.findIndex(item => item.id === row.id)
        
        if (itemIndex === -1) {
          console.error('Item não encontrado na lista')
          return
        }

        const item = state.listaPagamento[itemIndex]
        item.bloqueado_manual = !row.bloqueado_manual
        
        // ✅ ADICIONAR: Se desbloqueou manualmente, remove o bloqueio financeiro também
        if (!item.bloqueado_manual) {
          item.hasFinancialBlock = false
        }
        
        // Recalcula bloqueio financeiro (apenas se não foi desbloqueado manualmente)
        if (item.bloqueado_manual) {
          const today = moment().format('YYYY-MM-DD')
          const paymentDay = String(item.payment_day).padStart(2, '0')
          const dueDate = `${state.anoSelecionado}-${state.mesSelecionado}-${paymentDay}`
          const financialBlock = !item.payment_date && moment(dueDate).isBefore(today, 'day')
          item.hasFinancialBlock = financialBlock
        }

        // ✅ ATUALIZAR: Combina bloqueio manual + financeiro
        item.blockedIcon = item.bloqueado_manual || item.hasFinancialBlock

        swal.fire({
          icon: 'success',
          title: t('success'),
          text: item.bloqueado_manual ? t('studentBlocked') : t('studentUnblocked'),
          timer: 1500,
          showConfirmButton: false,
        })
      }
    })
    .catch((err) => {
      console.error(err)
      swal.fire('Erro', t('operationFailed'), 'error')
    })
}

function getBlockTooltip(row) {
  if (row.bloqueado_manual) {
    return t('clickToUnblock') // "Clique para desbloquear"
  } else if (row.hasFinancialBlock) {
    return t('financiallyBlocked') // "Bloqueado financeiramente - Clique para desbloquear manualmente"
  } else {
    return t('clickToBlock') // "Clique para bloquear manualmente"
  }
}

function formatPrice(value) {
  if (!value) return '0,00'
  return parseFloat(value).toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })
}


    onMounted(() => buscarDados())

    return {
      showForm,
      formularioTermo,
      colunas,
      state,
      detalhes,
      buscarDados,
      toggleBlock,
      getBlockTooltip,
      formatPrice,
      hasSelectedStudent
    }
  },
}
</script>